/**
 * The lobby — which rooms exist, and which one to walk into.
 *
 * *** THIS SCREEN DECIDES NOTHING ***
 * `may_join`, `refusal`, occupancy and opening state all arrive computed by
 * the server. A client that recalculated them would offer Enter on a room
 * that filled thirty seconds ago and the student would be refused for no
 * reason they could see.
 *
 * *** THE ROOM IS A SEPARATE SCREEN ***
 * Presence, chat and camera tiles all live at `/library/:id`. They were
 * briefly rendered inside these cards, which worked for a count and a chat
 * box and had nowhere to put a grid of video tiles. A lobby lists rooms; a
 * room is where you sit.
 *
 * *** EVERY REFUSAL SAYS WHICH ONE ***
 * This project has shipped the opposite three times — a chat composer that
 * said only "Best plan", a booking that said "pricing is not configured" on
 * a tenant that bills by subscription, a payment stuck on "pending" forever.
 * Each left someone with no action to take.
 */

import { useNavigate } from 'react-router-dom'
import { PageHeader, PageStack } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useJoinLibrary, useLibraries } from '@/lib/queries'
import type { LibraryCard, LibraryRefusal } from '@/types/api'
import { hoursMinutes } from './format'
import styles from './LibrariesPage.module.css'

/**
 * The badge, from the server's numbers.
 *
 * Deliberately not "nearly full" arithmetic invented here — the thresholds
 * are a product decision and belong in one place. What IS local is purely
 * presentational: which words wrap which number.
 */
function occupancyLabel(card: LibraryCard): string {
  if (!card.is_open_now && card.opens_at) {
    return `Opens at ${card.opens_at.slice(0, 5)}`
  }
  const free = card.capacity - card.students_inside
  if (free <= 0) return 'Full'
  if (free <= 2) return 'Almost full'
  return `${card.students_inside} studying now`
}

/**
 * What to tell someone who cannot go in.
 *
 * The server also sends a sentence; this is the short form for the row,
 * where a paragraph would not fit. The long form is shown in the toast when
 * they actually try.
 */
function refusalLabel(refusal: LibraryRefusal | null): string | null {
  switch (refusal) {
    case 'FULL':
      return 'Full'
    case 'CLOSED_NOW':
      return 'Closed now'
    case 'INACTIVE':
      return 'Unavailable'
    case 'NOT_ENTITLED':
      return 'Needs a plan'
    case 'ALREADY_INSIDE':
      return 'You are in another room'
    case 'NOT_A_MEMBER':
      return 'No access'
    default:
      return null
  }
}

export function LibrariesPage() {
  const { data, isLoading, isFetching, isSuccess, error } = useLibraries()
  const join = useJoinLibrary()
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()

  const isMentor = user?.role === 'MENTOR'
  const all = data?.libraries ?? []

  /*
    *** A MENTOR SEES ONLY THE ROOMS THEY SUPERVISE ***
    The server sends every active room to everyone — that is how the student
    lobby works — so a mentor used to get this screen showing rooms they have
    no business in, each with "No access" beside a dead button. Nothing told
    them which were theirs.

    Filtered rather than reordered: a mentor has no reason to walk into a
    room they are not assigned to, and an institute may run twenty.
  */
  /*
    *** A LIST WITH NO `i_supervise` AT ALL IS NOT A LIST OF NOTHING ***
    The field is optional, so a response from an older server — or a cached
    one written before this deploy — carries it on no card. Filtering such a
    list yields zero rooms and the screen would claim the mentor supervises
    none, which is a statement about what an administrator did, from data
    that never answered the question.

    When nothing says `i_supervise`, show every room instead: the server
    still marks the ungranted ones NOT_A_MEMBER, so the worst case is the
    old behaviour rather than a false claim.
  */
  const serverKnowsGrants = all.some((c) => c.i_supervise !== undefined)
  const libraries =
    isMentor && serverKnowsGrants ? all.filter((c) => c.i_supervise) : all

  const supervision = data?.my_supervision_seconds ?? null

  /*
    *** WHY "NO ROOMS" IS NOT SIMPLY `libraries.length === 0` ***
    A mentor was shown "You are not assigned to any room yet" for ten
    seconds, then the room appeared. Two things caused it and either alone
    is enough:

    1. `isLoading` is true only on the FIRST fetch with an empty cache. On a
       revisit React Query serves the cached list at once, so the screen was
       deciding "empty" from data it had not re-checked.
    2. A cache written before this deploy has no `i_supervise` at all, so
       the filter dropped every room until the next poll replaced it —
       ten seconds later, because that is the refetch interval.

    So the empty state waits for a settled response: this render's data must
    have come back AND no request may be in flight. Telling a mentor they
    have no rooms is a claim about an administrator's decision — it must
    never be made from a stale or half-known list.
  */
  const settled = isSuccess && !isFetching
  const reallyEmpty = settled && libraries.length === 0

  const enter = async (card: LibraryCard, switchFrom = false) => {
    /*
      *** ALREADY INSIDE: WALK IN, DO NOT JOIN AGAIN ***
      A second join would be refused as ALREADY_INSIDE and show an error for
      pressing the button on the room you are sitting in.
    */
    if (card.i_am_inside) {
      navigate(`/library/${card.library_id}`)
      return
    }

    try {
      await join.mutateAsync({ libraryId: card.library_id, switchFrom })
      navigate(`/library/${card.library_id}`)
    } catch (err) {
      /*
        *** THE SERVER'S OWN SENTENCE, NOT A GENERIC ONE ***
        It already names the library that is full, the time the room opens,
        or where the student currently is. Replacing it with "Could not
        enter" would throw away the only thing that says what to do next.
      */
      const message =
        err instanceof ApiError ? err.message : 'Could not enter this library.'
      const refusal =
        err instanceof ApiError
          ? (err.details?.refusal as LibraryRefusal | undefined)
          : undefined

      if (refusal === 'ALREADY_INSIDE' && !switchFrom) {
        const where =
          (err instanceof ApiError &&
            (err.details?.current_library_name as string | undefined)) ||
          'another room'
        toast.info(`${message} Leave ${where} first, or switch.`)
        return
      }
      toast.error(message)
    }
  }

  return (
    <PageStack>
      {/*
        *** NOT THE SAME SENTENCE FOR BOTH ***
        "Your time is recorded" is a promise to a student — it is the study
        total and the leaderboard. A mentor is not ranked on anything and
        their hours are supervision, not study, so telling them the same
        thing would describe a feature they do not have.
      */}
      <PageHeader
        title={isMentor ? 'Rooms you supervise' : 'Library'}
        description={
          isMentor
            ? 'Students can see when a mentor is in. Your hours on duty are recorded separately from study time.'
            : 'Study alongside others. Your time is recorded while you are here.'
        }
      />

      {isMentor && supervision !== null && (
        <p className={styles.dutyTotal}>
          {supervision > 0 ? (
            <>
              <strong>{hoursMinutes(supervision)}</strong> on duty, all time
            </>
          ) : (
            'No time on duty yet — enter a room to start.'
          )}
        </p>
      )}

      {/* Shown whenever there is nothing to display yet — including a
          refetch that has not landed, which is the window the false
          "no rooms" message used to fill. */}
      {(isLoading || (isFetching && libraries.length === 0)) && (
        <p className={styles.muted}>Loading libraries…</p>
      )}

      {error && (
        <p className={styles.error}>
          {error instanceof ApiError
            ? error.message
            : 'Could not load libraries.'}
        </p>
      )}

      {/*
        *** THE EMPTY STATE MUST SAY WHOSE JOB IT IS ***
        For a mentor it is almost never "no rooms exist" — it is "nobody has
        assigned you to one", which needs a different person to act. Telling
        them to wait for rooms to be added would send them nowhere.
      */}
      {reallyEmpty && !error && (
        <p className={styles.muted}>
          {isMentor
            ? all.length > 0
              ? 'You are not assigned to any room yet. An administrator grants library access from Libraries → Mentors.'
              : 'No libraries exist yet. Your institute will add them.'
            : 'No libraries are open yet. Your institute will add them.'}
        </p>
      )}

      <div className={styles.grid}>
        {libraries.map((card) => {
          const short = refusalLabel(card.refusal)
          const isElsewhere = card.refusal === 'ALREADY_INSIDE'
          const open = card.is_open_now

          return (
            <article
              key={card.library_id}
              className={`${styles.card} ${card.i_am_inside ? styles.cardActive : ''}`}
            >
              <div className={styles.cardBody}>
                <header className={styles.cardHead}>
                  <h3 className={styles.name}>{card.name}</h3>
                  <Pill tone={open ? 'success' : 'neutral'}>
                    {occupancyLabel(card)}
                  </Pill>
                </header>

                {card.description && (
                  <p className={styles.description}>{card.description}</p>
                )}

                <dl className={styles.meta}>
                  <div>
                    {/*
                      A mentor never consumes a seat, so free capacity is not
                      their decision — how many students are waiting is.
                    */}
                    <dt>{isMentor ? 'Students' : 'Seats'}</dt>
                    <dd>
                      {isMentor
                        ? card.students_inside === 0
                          ? 'Nobody here yet'
                          : card.students_inside === 1
                            ? '1 studying now'
                            : `${card.students_inside} studying now`
                        : `${card.capacity - card.students_inside} free of ${card.capacity}`}
                    </dd>
                  </div>
                  <div>
                    {/*
                      Stays "Mentors", not "Other mentors", for a mentor too:
                      `mentors_inside` counts everyone on duty INCLUDING the
                      viewer, so the narrower label would be a false claim on
                      the room they are sitting in.
                    */}
                    <dt>Mentors</dt>
                    <dd>
                      {/*
                        *** THE REASON A STUDENT PICKS THIS ROOM ***
                        Mentors never consume a seat, so they are counted
                        apart. Said in words rather than a bare number
                        because "a mentor is here to ask" is the fact that
                        decides whether to enter.
                      */}
                      {card.mentors_inside > 0 ? (
                        <span className={styles.mentorHere}>
                          {card.mentors_inside === 1
                            ? '1 mentor here'
                            : `${card.mentors_inside} mentors here`}
                        </span>
                      ) : (
                        'None right now'
                      )}
                    </dd>
                  </div>
                  {card.opens_at && card.closes_at && (
                    <div>
                      <dt>Hours</dt>
                      <dd>
                        {card.opens_at.slice(0, 5)} – {card.closes_at.slice(0, 5)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <footer className={styles.actions}>
                {card.i_am_inside ? (
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => enter(card)}
                  >
                    {isMentor ? 'Back on duty →' : 'Back to your seat →'}
                  </Button>
                ) : isElsewhere ? (
                  <Button fullWidth onClick={() => enter(card, true)}>
                    Move to this room
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => enter(card)}
                    disabled={!card.may_join || join.isPending}
                  >
                    {join.isPending
                      ? 'Entering…'
                      : isMentor
                        ? 'Go on duty →'
                        : 'Enter Library →'}
                  </Button>
                )}

                {/* The reason sits beside the disabled button. A greyed-out
                    control with no explanation is the failure this screen
                    exists to avoid. */}
                {!card.may_join && !card.i_am_inside && short && (
                  <span className={styles.refusal}>{short}</span>
                )}
              </footer>
            </article>
          )
        })}
      </div>
    </PageStack>
  )
}
